/**
 * MiniGameRegistry - Central registry for mini-game types
 *
 * Games register themselves on import.
 * This allows the system to be extensible without hardcoding game types.
 */
class MiniGameRegistryClass {
    definitions = new Map();
    /**
     * Register a new mini-game type.
     * Called by each game file on import.
     */
    register(definition) {
        if (this.definitions.has(definition.type)) {
            console.warn(`Mini-game type "${definition.type}" is already registered`);
            return;
        }
        this.definitions.set(definition.type, definition);
    }
    /**
     * Get a game definition by type/handler.
     */
    get(type) {
        return this.definitions.get(type);
    }
    /**
     * Check if a game type/handler is registered.
     */
    isRegistered(type) {
        return this.definitions.has(type);
    }
    /**
     * Get all registered game types.
     */
    getRegisteredTypes() {
        return Array.from(this.definitions.keys());
    }
    /**
     * Create an engine instance for a config.
     * Validates the config first and throws if invalid — callers do not need a
     * separate validate() call before createEngine().
     */
    createEngine(config) {
        const handler = config.handler;
        const definition = this.definitions.get(handler);
        if (!definition) {
            throw new Error(`Unknown mini-game handler: "${handler}"`);
        }
        const validation = definition.validate(config);
        if (!validation.valid) {
            throw new Error(`Invalid mini-game config for "${handler}": ${validation.errors.join(", ")}`);
        }
        return definition.createEngine(config);
    }
    /**
     * Validate a config for a game type.
     */
    validate(config) {
        const handler = config.handler;
        const definition = this.definitions.get(handler);
        if (!definition) {
            return {
                valid: false,
                errors: [`Unknown mini-game handler: "${handler}"`],
            };
        }
        return definition.validate(config);
    }
    /**
     * Get instruction text for a game.
     */
    getInstructionText(config) {
        const handler = config.handler;
        const definition = this.definitions.get(handler);
        return definition?.getInstructionText(config) || "Complete the puzzle!";
    }
    /**
     * Check if a game type supports hints.
     */
    supportsHints(config) {
        const handler = config.handler;
        return this.definitions.get(handler)?.supportsHints ?? false;
    }
    /**
     * Get the interaction type for a game (drag, click, or input).
     */
    getInteractionType(config) {
        const handler = config.handler;
        return this.definitions.get(handler)?.interactionType ?? "drag";
    }
    /**
     * Extract targets from FEN for a game type.
     * The FEN and player color are read from the config.
     */
    extractTargetsFromFen(config) {
        const handler = config.handler;
        const definition = this.definitions.get(handler);
        return (definition?.extractTargetsFromFen?.(config.fen, config.playerColor) || []);
    }
    /**
     * Get UI configuration for a game type.
     */
    getUIConfig(type) {
        const definition = this.definitions.get(type);
        return definition?.uiConfig || {};
    }
}
export const MiniGameRegistry = new MiniGameRegistryClass();
